import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { FlowTypes, IClientAccounts } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { ClientAccountsService } from '@shared/services/client-accounts/client-accounts.service';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  closeSpinner,
  Go,
  LoadClientAccounts,
  LoadClientAccountsFail,
  LoadClientAccountsStop,
  LoadClientAccountsSuccess,
  LogError,
} from '../../actions';
import * as fromRootStore from '../../index';

type ActionList = (
  | ({
      payload: IClientAccounts;
    } & Action<'[Client Accounts] Load Client Accounts Data Success'>)
  | Action<'[Client Eligibility] Check Client Eligibility'>
  | Action<'[Loader] Close Spinner'>
)[];

@Injectable()
export class ClientAccountsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private clientAccountsService: ClientAccountsService,
    private router: Router,
  ) {}

  loadClientAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadClientAccounts),
      withLatestFrom(this.store$),
      switchMap(([, storeState]) => {
        if (!storeState.clientAccounts.loaded) {
          return this.clientAccountsService.getClientAccounts().pipe(
            switchMap((entity) => {
              if (storeState.triage.flowType === FlowTypes.GIFTING) {
                entity.accounts = entity.accounts.map((account) => {
                  const { positions, ...accountRest } = account;
                  const validPositions = positions.filter(
                    (position) => position.balance && Number(position.balance),
                  );
                  return { ...accountRest, positions: validPositions };
                });
              }
              const actions: ActionList = [LoadClientAccountsSuccess({ payload: entity })];
              actions.push(closeSpinner());
              return actions;
            }),
            catchError((err) => of(LoadClientAccountsFail({ payload: err }))),
          );
        } else {
          const isInheritanceFlow =
            storeState.triage.flowType === FlowTypes.MULTI_INHERITANCE ||
            storeState.triage.flowType === FlowTypes.INHERITANCE;
          const isSaveStateEnabled = storeState.featureToggles?.cwp_inheritance_save_state;
          return isInheritanceFlow && isSaveStateEnabled
            ? [LoadClientAccountsStop(), closeSpinner()]
            : [LoadClientAccountsStop()];
        }
      }),
    ),
  );

  clientAccountFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadClientAccountsFail),
      map((action) => action.payload),
      switchMap((payload) => {
        return [
          Go({ path: ['error'] }),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-route-clientAccountsEffect',
              message: `Failed to retrieve client account data, here is the error: ${JSON.stringify(payload)}`,
              code: LoggerCodes.CLIENT_ACCOUNT_PROCESSING_EXCEPTION,
            },
          }),
        ];
      }),
    ),
  );
}
